'use client';
import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Lock, Eye } from 'lucide-react';

// Backend integration point: GET /api/bookings/:id/address-reveal-status
// Address reveal is permitted only 3 hours before booking start time
const confirmedBooking = {
  id: 'booking-conf-001',
  workerName: 'Thandi M.',
  date: '6 May 2026',
  time: '07:00',
  suburb: 'Waterkloof Ridge',
  // minutesUntilReveal: minutes until 3-hour window opens
  minutesUntilReveal: 142, // 2h22m until reveal window
  address: '14 Acacia Street, Waterkloof Ridge', // only shown when revealed
};

function formatCountdown(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function EmployerAddressReveal() {
  const [revealed, setRevealed] = useState(false);
  const [remaining, setRemaining] = useState(confirmedBooking.minutesUntilReveal);
  const canReveal = remaining <= 0;

  useEffect(() => {
    if (remaining <= 0) return;
    const timer = setInterval(() => {
      setRemaining((prev) => Math.max(0, prev - 1));
    }, 60000);
    return () => clearInterval(timer);
  }, [remaining]);

  return (
    <div
      className="card p-5 border-l-4"
      style={{ borderLeftColor: canReveal ? 'var(--success)' : 'var(--warning)', background: canReveal ? 'var(--success-bg)' : 'var(--warning-bg)' }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: canReveal ? 'var(--success)' : 'var(--warning)' }}>
            {canReveal ? <MapPin size={20} color="white" /> : <Lock size={20} color="white" />}
          </div>
          <div>
            <p className="font-bold text-sm" style={{ color: canReveal ? 'var(--success)' : 'var(--warning)' }}>
              {canReveal ? 'Address now available' : 'Address locked until 3 hours before your booking'}
            </p>
            <p className="text-xs mt-0.5" style={{ color: canReveal ? 'var(--success)' : 'var(--warning)', opacity: 0.85 }}>
              Booking with <strong>{confirmedBooking.workerName}</strong> on {confirmedBooking.date} at {confirmedBooking.time} · {confirmedBooking.suburb}
            </p>
            {!canReveal && (
              <p className="text-xs mt-1 flex items-center gap-1" style={{ color: 'var(--warning)' }}>
                <Clock size={12} />
                Address reveals in <strong className="tabular-nums">{formatCountdown(remaining)}</strong>
              </p>
            )}
          </div>
        </div>

        {canReveal && (
          <div>
            {revealed ? (
              <div className="px-4 py-2.5 rounded-xl text-sm font-bold" style={{ background: 'var(--success)', color: 'white' }}>
                <MapPin size={14} className="inline mr-1" />
                {confirmedBooking.address}
              </div>
            ) : (
              <button
                onClick={() => setRevealed(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-150 active:scale-95"
                style={{ background: 'var(--success)', color: 'white' }}
              >
                <Eye size={15} />
                Reveal address
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}